import { Component, TemplateRef, OnInit, OnDestroy } from '@angular/core';
import { NgbOffcanvas, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil, Subscription } from 'rxjs';
import { BankListAvailModel } from 'src/app/models/bank-list-avail.model';
import { BankService } from 'src/app/services/api/bank.service';
import { ToastService } from 'src/app/services/toast.service';
import {
  BankModel,
  StoreBankPayload,
  UpdateBankPayload,
} from 'src/app/models/bank.model';
import { ModalPinComponent } from '../profile/components/modal-pin-verification/modal-pin-verification.component';
import { ResponseCustomType } from 'src/app/types/response/response.type';
import { ProfileResponseType } from 'src/app/types/response/profile-response.type';
import { BuatPinComponent } from 'src/app/pages/dashboard/components/modal-buat-pin/buat-pin.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { BypassApiService } from 'src/app/services/api/bypass.service';
import { ProfileService } from 'src/app/services/profile.service';
import { environment } from 'src/environments/environment';

@Component({
  standalone: false,
  selector: 'app-rekening',
  templateUrl: './rekening.component.html',
  styleUrls: ['./rekening.component.scss'],
})
export class RekeningComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  bankNameList: BankListAvailModel[] = [];
  bankList: BankModel[] | null = null;
  addDisable: boolean = false;
  profile!: ProfileResponseType;
  loading: boolean = true;
  isEnterprise: boolean = false;
  pinToken: string = '';

  private profileSubscription!: Subscription;

  constructor(
    private offcanvasService: NgbOffcanvas,
    private bankService: BankService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private byPassService: BypassApiService,
    private profileService: ProfileService,
    private message: NzMessageService
  ) {}

  ngOnInit(): void {
    this.getProfile();
  }

  getProfile() {
    this.profileSubscription = this.profileService.profileData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.profile = data as any;
          this.isEnterprise = data.rajaongkir_level === 3;
          this.getBankName();
          this.getBankList();
        }
      });
  }

  openAddRek(content: TemplateRef<unknown>) {
    this.offcanvasService.open(content, {
      position: 'end',
      backdrop: 'static',
    });
  }

  closeAddRek(content: TemplateRef<unknown>) {
    this.pinToken = '';
    this.offcanvasService.dismiss(content);
  }

  onStartEdit(item: BankModel) {
    this.bankList?.forEach((b) => {
      if (b.id !== item.id) {
        b.isDisable = true;
      }
    });
  }

  getBankName(): void {
    if (this.isEnterprise) {
      this.bankService
        .bankListAvail()
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            this.bankNameList = res.data;
          },
          (err) => {
            this.message.create('error', err.error.meta.message);
          }
        );
    } else {
      this.byPassService
        .request<ResponseCustomType<BankListAvailModel[]>>({
          method: 'GET',
          endpoint: `${environment.SWITCH_URL}/user/api/v1/bank-account/list-bank-available`,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.bankNameList = res.data;
          },
          error: (err) => {
            this.message.create('error', err.error.meta.message);
          },
        });
    }
  }

  addRekening(payload: StoreBankPayload, content: TemplateRef<unknown>): void {
    if (this.isEnterprise) {
      this.bankService
        .bankStore(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            if (res.meta.code === 201) {
              this.toastService.show(
                null,
                'Successfully Added Bank Account',
                'success',
                {
                  classname: 'bg-success',
                  delay: 5000,
                }
              );
              this.pinToken = '';
              this.closeAddRek(content);
              this.getBankList();
            }
          },
          (err) => {
            this.message.create('error', err.error.meta.message);
          }
        );
    } else {
      this.byPassService
        .request<ResponseCustomType<unknown>>({
          method: 'POST',
          endpoint: `${environment.SWITCH_URL}/user/api/v1/bank-account/store`,
          body: payload,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.toastService.show(
              null,
              'Successfully Added Bank Account',
              'success',
              {
                classname: 'bg-success',
                delay: 5000,
              }
            );
            this.pinToken = '';
            this.closeAddRek(content);
            this.getBankList();
          },
          error: (err) => {
            this.message.create('error', err.error.meta.message);
          },
        });
    }
  }

  getBankList(): void {
    this.loading = true;
    if (this.isEnterprise) {
      this.bankService
        .bankList()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.bankList = res.data;
            this.addDisable = this.bankList ? this.bankList.length >= 6 : false;

            this.bankList?.forEach((item) => {
              item.isDisable = true;
              item.isEmptyBank = false;
              item.isEmptyRek = false;
              item.isEmptyOwn = false;
            });
            this.loading = false;
          },
          error: (err) => {
            this.loading = false;
            this.message.create('error', err.error?.meta?.message || 'Failed to fetch bank accounts');
          }
        });
    } else {
      this.byPassService
        .request<ResponseCustomType<BankModel[]>>({
          method: 'GET',
          endpoint: `${environment.SWITCH_URL}/user/api/v1/bank-account/list`,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.bankList = res.data;
            this.addDisable = this.bankList ? this.bankList.length >= 6 : false;

            this.bankList?.forEach((item) => {
              item.isDisable = true;
              item.isEmptyBank = false;
              item.isEmptyRek = false;
              item.isEmptyOwn = false;
            });
            this.loading = false;
          },
          error: (err) => {
            this.loading = false;
            this.message.create('error', err.error?.meta?.message || 'Failed to fetch bank accounts');
          },
        });
    }
  }

  editBank(payload: UpdateBankPayload): void {
    if (this.isEnterprise) {
      this.bankService
        .bankUpdate(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            if (res.meta.code === 200) {
              this.toastService.show(
                null,
                'Successfully Edit Bank Account',
                'success',
                {
                  classname: 'bg-success',
                  delay: 5000,
                }
              );
              this.getBankList();
            }
          },
          (err) => {
            this.message.create('error', err.error.meta.message);
          }
        );
    } else {
      this.byPassService
        .request<ResponseCustomType<unknown>>({
          method: 'PUT',
          endpoint: `${environment.SWITCH_URL}/user/api/v1/bank-account/update`,
          body: payload,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.toastService.show(
              null,
              'Successfully Edit Bank Account',
              'success',
              {
                classname: 'bg-success',
                delay: 5000,
              }
            );
            this.getBankList();
          },
          error: (err) => {
            this.message.create('error', err.error.meta.message);
          },
        });
    }
  }

  cancelEdit(item?: BankModel) {
    if (item) {
      item.isDisable = true;
      item.pin_token = '';
    }
    this.getBankList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
    }
  }
}
